/**
 * Generates a search modal and appends it to a specified container.
 *
 * @param {string} container_id - The ID of the container element where the modal will be appended.
 * @param {Object} data - The data object used to populate the modal.
 */
export function gen_search_modal(container_id, data) {
    const modal_id = `${container_id}_modal`;

    gen_modal(modal_id);
    gen_search_bar(container_id);
}

/**
 * Updates or generates a Select2 search bar within a modal.
 *
 * @param {string} container_id - The ID of the container element where the search bar will be appended.
 * @param {Object} data - The data object used to populate the search bar.
 * @param {boolean} change_tab - A flag indicating whether the tab has changed.
 */
export function update_select2_search_bar(container_id, data, change_tab) {
    const modal_id = `${container_id}_modal`;
    const modal_container = document.getElementById(modal_id)

    // generate search bar modal
    gen_search_modal(container_id, data);

    // Update Select
    if (!modal_container || modal_container.children.length === 0 || change_tab) {
        // if no item was selected or tab changed(need to init the search modal)
        gen_select(modal_id, data);
    } 
}

/**
 * Generates a searchable, single-selection organization dropdown.
 *
 * @param {string} container_id - The ID of the container for the dropdown.
 * @param {Array} organizations - Organization objects with name, title, and tracking.
 * @param {string} default_org_name - The organization name to select initially.
 * @returns {HTMLSelectElement} The select element; callers can listen for its change event.
 */
export function gen_organization_select(container_id, organizations, default_org_name) {
    const container = document.getElementById(container_id);
    if (!container) {
        throw new Error(`Organization select container not found: ${container_id}`);
    }

    const select_id = `${container_id}_select`;
    const previous_select = document.getElementById(select_id);
    if (previous_select) {
        if ($(previous_select).hasClass('select2-hidden-accessible')) {
            $(previous_select).select2('destroy');
        }
        previous_select.remove();
    }

    const sorted_organizations = organizations.slice().sort(
        (a, b) => Number(b.tracking) - Number(a.tracking)
    );
    const title_counts = new Map();
    sorted_organizations.forEach(org => {
        const title = org.title || org.name;
        title_counts.set(title, (title_counts.get(title) || 0) + 1);
    });

    const selected_name = sorted_organizations.some(org => org.name === default_org_name)
        ? default_org_name
        : sorted_organizations[0]?.name;

    const select = document.createElement('select');
    select.id = select_id;
    select.name = 'organization';
    select.className = 'form-select rounded-5';

    sorted_organizations.forEach(org => {
        const option = document.createElement('option');
        const title = org.title || org.name;
        option.value = org.name;
        option.textContent = title_counts.get(title) > 1
            ? `${title}（${org.name}）`
            : title;
        option.selected = org.name === selected_name;
        select.appendChild(option);
    });

    container.appendChild(select);
    $(select).select2({
        theme: 'bootstrap-5',
        selectionCssClass: 'select2--small',
        dropdownCssClass: 'select2--small',
        width: '100%',
    });

    return select;
}

/**
 * Generates a Select2 dropdown within a modal.
 *
 * @param {string} modal_id - The ID of the modal element where the dropdown will be appended.
 * @param {Object} data - The data object used to populate the dropdown.
 */
function gen_select(modal_id, data) {
    const container = document.querySelector(`#${modal_id} .modal-body`);

    const select = document.createElement("select");
    select.className = "js-example-basic-multiple form-select mb-3";
    select.id = `${modal_id}_select`
    select.name = "states[]";
    select.setAttribute("multiple", "multiple");

    const all_data = Array.from(data.entries());
    const default_data_arr = all_data.slice(0, 10);  // default selection: top 10 data
    const display_data_arr = all_data.slice(10);  // other data

    // Set the top 10 data to the selected options
    default_data_arr.forEach(([key, value]) => {
        const option = document.createElement("option");
        option.textContent = key;
        option.value = key;
        option.selected = true;  // selected
        select.appendChild(option);
    });

    // Append other data to options
    display_data_arr.forEach(([key, value]) => {
        const option = document.createElement("option");
        option.textContent = key;
        option.value = key;
        select.appendChild(option);
    });

    container.appendChild(select);

    $(`#${select.id}`).select2({
        theme: "bootstrap-5",
        selectionCssClass: "select2--small",
        dropdownCssClass: "select2--small",
        dropdownParent: $(`#${modal_id}`),
        placeholder: "Select Items",
    });
}

/**
 * Generates a modal element and appends it to the main element.
 *
 * @param {string} desired_id - The desired ID for the modal element.
 */
function gen_modal(desired_id) {
    let main = document.getElementsByTagName("main")[0];

    let modal = document.createElement('div');
    modal.classList.add('modal', 'fade');
    modal.id = desired_id;
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('aria-labelledby', 'ModalLabel');
    modal.setAttribute('aria-hidden', 'true');

    let modal_dialog = document.createElement('div');
    modal_dialog.classList.add('modal-dialog', 'modal-lg');

    let modal_content = document.createElement('div');
    modal_content.classList.add('modal-content');

    let modal_header = document.createElement('div');
    modal_header.classList.add('modal-header');

    let modal_title = document.createElement('h5');
    modal_title.classList.add('modal-title');
    modal_title.id = `${desired_id}_label`;
    modal_title.textContent = 'Search';

    let close_btn = document.createElement('button');
    close_btn.classList.add('btn-close');
    close_btn.setAttribute('type', 'button');
    close_btn.setAttribute('data-bs-dismiss', 'modal');
    close_btn.setAttribute('aria-label', 'Close');

    let modal_body = document.createElement('div');
    modal_body.classList.add('modal-body');

    let modal_footer = document.createElement('div');
    modal_footer.classList.add('modal-footer');

    let close_footer_btn = document.createElement('button');
    close_footer_btn.classList.add('btn', 'btn-secondary');
    close_footer_btn.setAttribute('type', 'button');
    close_footer_btn.setAttribute('data-bs-dismiss', 'modal');
    close_footer_btn.textContent = 'Close';

    let save_btn = document.createElement('button');
    save_btn.classList.add('btn', 'btn-primary');
    save_btn.setAttribute('type', 'button');
    save_btn.setAttribute('data-bs-dismiss', 'modal');
    save_btn.id = `${desired_id}_save_btn`;
    save_btn.textContent = 'Add Lines';

    modal_header.appendChild(modal_title);
    modal_header.appendChild(close_btn);

    modal_footer.appendChild(close_footer_btn);
    modal_footer.appendChild(save_btn);

    modal_content.appendChild(modal_header);
    modal_content.appendChild(modal_body);
    modal_content.appendChild(modal_footer);

    modal_dialog.appendChild(modal_content);
    modal.appendChild(modal_dialog);

    main.appendChild(modal);
}

/**
 * Generates a search bar and appends it to a specified container.
 *
 * @param {string} container_id - The ID of the container element where the search bar will be appended.
 */
function gen_search_bar(container_id) {
    const container = document.getElementById(container_id);

    if(!container || container.children.length === 0) {
        const div = document.createElement("div");
        const text = document.createElement("p");
        const button = document.createElement("button");
        const search_container = document.createElement("span");
        const search_icon = document.createElement("i");
        const place_holder = document.createElement("span");

        text.textContent = "+ 尋找其他項目";
        text.className = "mb-0 pb-0 mx-0";
        text.style = "font-size: 12px";
        text.setAttribute("data-i18n", "search_item");
        $(text).localize();

        button.type = "button"
        button.className = "btn btn-outline-secondary btn-sm search-btn text-start";
        button.setAttribute("data-bs-toggle", "modal");
        button.setAttribute("data-bs-target", `#${container_id}_modal`)

        place_holder.textContent = "Search";
        search_icon.className = "bi bi-search";
        search_container.appendChild(search_icon);
        search_container.appendChild(place_holder);
        button.appendChild(search_container);

        container.appendChild(button);
        container.appendChild(text);
    }

    return;
}
